/**
 * PURPOSE: Estado de servidor del módulo de Resultados de Atletismo en React Query.
 * RESPONSIBILITIES: Catálogo de pruebas, detalle de una prueba, clasificación por universidad y
 *   ajustes de publicación, más las escrituras con su refresco.
 *
 *   EL SERVIDOR MANDA. Toda escritura de una prueba devuelve el `PruebaDetail` ya recalculado
 *   (puestos, `best`, `qualified`, `advancesToFinal`, `standings`). Ese detalle reemplaza la caché tal
 *   cual — sin optimistic update ni cálculo local — y después se invalidan el catálogo (progreso,
 *   estado), la clasificación por universidad y, en una sub-prueba de combinadas, el detalle de la
 *   combinada (puntos WA).
 *
 *   SIN REINTENTOS. Los errores del módulo son decisiones (403, 404, 409), no fallos de red.
 * COLLABORATORS: services/athleticsResultsService.js, area/AtletismoResultadosArea. Mismo patrón que
 *   useProgrammingCenterData.js.
 * ROUTE: winscore/src/hooks/useAthleticsResults.js
 */
import { useCallback, useMemo } from "react";
import { useQuery, useQueryClient } from "react-query";

import {
  addAthleticsAthlete,
  addAthleticsRound,
  addAthleticsRoundEntry,
  addAthleticsTeam,
  buildAthleticsFinal,
  getAthleticsPrueba,
  getAthleticsSettings,
  getAthleticsStandings,
  listAthleticsPruebas,
  moveAthleticsEntry,
  officializeAthleticsPrueba,
  removeAthleticsRoundEntry,
  removeAthleticsRound,
  reopenAthleticsPrueba,
  saveAthleticsRound,
  searchAthleticsAthleteCandidates,
  searchAthleticsTeamCandidates,
  updateAthleticsSettings,
  updateAthleticsVisibility,
} from "../services/athleticsResultsService.js";

export const ATHLETICS_RESULTS_QUERY_ROOT = "athletics-results";

export const athleticsResultsKeys = {
  root: [ATHLETICS_RESULTS_QUERY_ROOT],
  scope: (idevent, idsport) => [ATHLETICS_RESULTS_QUERY_ROOT, Number(idevent), Number(idsport)],
  pruebas: (idevent, idsport) => [ATHLETICS_RESULTS_QUERY_ROOT, Number(idevent), Number(idsport), "pruebas"],
  prueba: (idevent, idsport, pruebaId) => [
    ATHLETICS_RESULTS_QUERY_ROOT,
    Number(idevent),
    Number(idsport),
    "prueba",
    Number(pruebaId),
  ],
  standings: (idevent, idsport) => [ATHLETICS_RESULTS_QUERY_ROOT, Number(idevent), Number(idsport), "standings"],
  settings: (idevent, idsport) => [ATHLETICS_RESULTS_QUERY_ROOT, Number(idevent), Number(idsport), "settings"],
};

const QUERY_OPTIONS = {
  retry: false,
  refetchOnWindowFocus: false,
  staleTime: 15 * 1000,
};

const isValidId = (value) => Number.isInteger(Number(value)) && Number(value) > 0;

const useScope = (idevent, idsport) =>
  useMemo(() => ({ idevent: Number(idevent), idsport: Number(idsport) }), [idevent, idsport]);

export const useAthleticsPruebas = ({ idevent, idsport, enabled = true }) => {
  const scope = useScope(idevent, idsport);
  return useQuery(
    athleticsResultsKeys.pruebas(scope.idevent, scope.idsport),
    () => listAthleticsPruebas(scope),
    { ...QUERY_OPTIONS, enabled: enabled && isValidId(scope.idevent) && isValidId(scope.idsport) }
  );
};

export const useAthleticsPrueba = ({ idevent, idsport, pruebaId, enabled = true }) => {
  const scope = useScope(idevent, idsport);
  return useQuery(
    athleticsResultsKeys.prueba(scope.idevent, scope.idsport, pruebaId),
    () => getAthleticsPrueba({ ...scope, pruebaId }),
    {
      ...QUERY_OPTIONS,
      enabled:
        enabled && isValidId(scope.idevent) && isValidId(scope.idsport) && isValidId(pruebaId),
    }
  );
};

export const useAthleticsStandings = ({ idevent, idsport, enabled = true }) => {
  const scope = useScope(idevent, idsport);
  return useQuery(
    athleticsResultsKeys.standings(scope.idevent, scope.idsport),
    () => getAthleticsStandings(scope),
    { ...QUERY_OPTIONS, enabled: enabled && isValidId(scope.idevent) && isValidId(scope.idsport) }
  );
};

export const useAthleticsSettings = ({ idevent, idsport, enabled = true }) => {
  const scope = useScope(idevent, idsport);
  return useQuery(
    athleticsResultsKeys.settings(scope.idevent, scope.idsport),
    () => getAthleticsSettings(scope),
    { ...QUERY_OPTIONS, enabled: enabled && isValidId(scope.idevent) && isValidId(scope.idsport) }
  );
};

/** Copia en el catálogo los campos de resumen de una prueba recién escrita (sin `config`). */
const mergeSummaryIntoList = (list, summary) => {
  if (!list || !Array.isArray(list.pruebas) || !summary) return list;
  const { config: _config, ...rest } = summary;
  return {
    ...list,
    pruebas: list.pruebas.map((item) =>
      Number(item.id) === Number(rest.id) ? { ...item, ...rest } : item
    ),
  };
};

/**
 * Escrituras del módulo. Cada una devuelve lo que respondió el servidor y deja la caché al día;
 * los errores se propagan sin tocar para que la pantalla lea `code`/`details`.
 */
export const useAthleticsResultsActions = ({ idevent, idsport }) => {
  const queryClient = useQueryClient();
  const scope = useScope(idevent, idsport);

  const refreshAggregates = useCallback(
    (parentId) => {
      const jobs = [
        queryClient.invalidateQueries(athleticsResultsKeys.pruebas(scope.idevent, scope.idsport)),
        queryClient.invalidateQueries(athleticsResultsKeys.standings(scope.idevent, scope.idsport)),
      ];
      if (isValidId(parentId)) {
        jobs.push(
          queryClient.invalidateQueries(
            athleticsResultsKeys.prueba(scope.idevent, scope.idsport, parentId)
          )
        );
      }
      return Promise.all(jobs).catch(() => undefined);
    },
    [queryClient, scope.idevent, scope.idsport]
  );

  const applyDetail = useCallback(
    (detail) => {
      const summary = detail?.prueba;
      if (summary && isValidId(summary.id)) {
        queryClient.setQueryData(
          athleticsResultsKeys.prueba(scope.idevent, scope.idsport, summary.id),
          detail
        );
        queryClient.setQueryData(athleticsResultsKeys.pruebas(scope.idevent, scope.idsport), (old) =>
          mergeSummaryIntoList(old, summary)
        );
      }
      refreshAggregates(summary?.parentId);
      return detail;
    },
    [queryClient, refreshAggregates, scope.idevent, scope.idsport]
  );

  const saveRound = useCallback(
    async ({ pruebaId, roundId, payload }) =>
      applyDetail(await saveAthleticsRound({ ...scope, pruebaId, roundId, payload })),
    [applyDetail, scope]
  );

  const addEntry = useCallback(
    async ({ pruebaId, roundId, entryId, teamId, lane, expectedRevision, ...occupied }) =>
      applyDetail(
        await addAthleticsRoundEntry({ ...scope, pruebaId, roundId, entryId, teamId, lane, expectedRevision, ...occupied })
      ),
    [applyDetail, scope]
  );

  const removeEntry = useCallback(
    async ({ pruebaId, roundId, rowId, expectedRevision, confirmResult }) =>
      applyDetail(
        await removeAthleticsRoundEntry({ ...scope, pruebaId, roundId, rowId, expectedRevision, confirmResult })
      ),
    [applyDetail, scope]
  );

  /** Agregar un deportista a la prueba (inscripción + serie); además cambia el contador de inscritos de las categorías. */
  const addAthlete = useCallback(
    async ({ pruebaId, idacreditation, roundId, lane, expectedRevision, ...occupied }) =>
      applyDetail(await addAthleticsAthlete({ ...scope, pruebaId, idacreditation, roundId, lane, expectedRevision, ...occupied })),
    [applyDetail, scope]
  );

  const moveEntry = useCallback(
    async ({ pruebaId, roundId, rowId, ...rest }) =>
      applyDetail(await moveAthleticsEntry({ ...scope, pruebaId, roundId, rowId, ...rest })),
    [applyDetail, scope]
  );

  const searchCandidates = useCallback(
    ({ pruebaId, query }) => searchAthleticsAthleteCandidates({ ...scope, pruebaId, query }),
    [scope]
  );

  /** Institución como equipo de posta (reutiliza su equipo sin serie o crea uno sin relevistas). */
  const addTeam = useCallback(
    async ({ pruebaId, ...rest }) => applyDetail(await addAthleticsTeam({ ...scope, pruebaId, ...rest })),
    [applyDetail, scope]
  );

  const searchTeamCandidates = useCallback(({ pruebaId }) => searchAthleticsTeamCandidates({ ...scope, pruebaId }), [scope]);

  /** Agregar una serie / quitar una serie vacía: cambian las pestañas de la prueba (y el catálogo). */
  const addRound = useCallback(
    async ({ pruebaId, expectedRevision }) => applyDetail(await addAthleticsRound({ ...scope, pruebaId, expectedRevision })),
    [applyDetail, scope]
  );

  const removeRound = useCallback(
    async ({ pruebaId, roundId, expectedRevision }) =>
      applyDetail(await removeAthleticsRound({ ...scope, pruebaId, roundId, expectedRevision })),
    [applyDetail, scope]
  );

  const buildFinal = useCallback(
    async ({ pruebaId, expectedRevision }) =>
      applyDetail(await buildAthleticsFinal({ ...scope, pruebaId, expectedRevision })),
    [applyDetail, scope]
  );

  const officialize = useCallback(
    async ({ pruebaId, expectedRevision }) =>
      applyDetail(await officializeAthleticsPrueba({ ...scope, pruebaId, expectedRevision })),
    [applyDetail, scope]
  );

  const reopen = useCallback(
    async ({ pruebaId, expectedRevision }) =>
      applyDetail(await reopenAthleticsPrueba({ ...scope, pruebaId, expectedRevision })),
    [applyDetail, scope]
  );

  /** La visibilidad responde un `PruebaSummary`: se mezcla en el detalle conservando `config` y series. */
  const setVisibility = useCallback(
    async ({ pruebaId, visibility }) => {
      const summary = await updateAthleticsVisibility({ ...scope, pruebaId, visibility });
      if (summary && isValidId(summary.id)) {
        queryClient.setQueryData(
          athleticsResultsKeys.prueba(scope.idevent, scope.idsport, summary.id),
          (old) =>
            old && old.prueba
              ? { ...old, prueba: { ...old.prueba, ...summary, config: old.prueba.config } }
              : old
        );
        queryClient.setQueryData(athleticsResultsKeys.pruebas(scope.idevent, scope.idsport), (old) =>
          mergeSummaryIntoList(old, summary)
        );
      }
      queryClient
        .invalidateQueries(athleticsResultsKeys.pruebas(scope.idevent, scope.idsport))
        .catch(() => undefined);
      return summary;
    },
    [queryClient, scope]
  );

  const saveSettings = useCallback(
    async (settings) => {
      const saved = await updateAthleticsSettings({ ...scope, settings });
      queryClient.setQueryData(athleticsResultsKeys.settings(scope.idevent, scope.idsport), saved);
      return saved;
    },
    [queryClient, scope]
  );

  return useMemo(
    () => ({
      saveRound,
      addEntry,
      removeEntry,
      addAthlete,
      moveEntry,
      searchCandidates,
      addTeam,
      searchTeamCandidates,
      addRound,
      removeRound,
      buildFinal,
      officialize,
      reopen,
      setVisibility,
      saveSettings,
    }),
    [
      addAthlete,
      addEntry,
      addRound,
      addTeam,
      buildFinal,
      moveEntry,
      officialize,
      removeEntry,
      removeRound,
      reopen,
      saveRound,
      saveSettings,
      searchCandidates,
      searchTeamCandidates,
      setVisibility,
    ]
  );
};
